import { createRoute, Link, useParams } from "@tanstack/react-router";
import { Eye } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopCaseDetail } from "./desktop/case-detail.tsx";
import { SubPage } from "../components/layout.tsx";
import { ErrorBlock, Img, Price, Spinner } from "../components/ui.tsx";
import { useCaseDetail } from "../api/queries.ts";

/**
 * 案例详情。
 *
 * 🔴 **里面的商品卡片没有静态价格。** 价格是服务端按当前访问者的归属链算出来的 ——
 *    同一篇案例，挂在不同分销商名下的人看到的是两个数。
 *    所以这一页不能缓存到 CDN，也不能把价格随内容一起发布。
 */
function CaseDetail() {
  const { slug } = useParams({ from: "/cases/$slug" });
  const { data, isPending, error, refetch } = useCaseDetail(slug);

  if (isPending) {
    return (
      <SubPage title="案例">
        <Spinner />
      </SubPage>
    );
  }
  if (error) {
    return (
      <SubPage title="案例">
        <ErrorBlock error={error} onRetry={() => refetch()} />
      </SubPage>
    );
  }

  return (
    <SubPage title={data.title}>
      <Img src={data.coverUrl} alt={data.title} className="aspect-[4/3] w-full object-cover" />

      <section className="bg-white px-4 py-4">
        <h1 className="text-base font-medium leading-snug">{data.title}</h1>
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500">
          {data.style && <span>{data.style}</span>}
          {data.areaSqm !== null && <span>{data.areaSqm} ㎡</span>}
          {data.budget !== null && (
            <span>
              预算 <Price cents={data.budget} />
            </span>
          )}
          <span className="flex items-center gap-0.5">
            <Eye className="size-3" />
            {data.viewCount}
          </span>
        </p>
      </section>

      {data.contentMd && (
        <section className="mt-2 bg-white px-4 py-4">
          {/* ⚠️ 不渲染 HTML —— 内容是后台写的纯文本，按原样保留换行就够，
              上 markdown 渲染器等于给自己开一个 XSS 面 */}
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-500">
            {data.contentMd}
          </p>
        </section>
      )}

      {data.imageUrls.length > 0 && (
        <section className="mt-2 bg-white">
          {data.imageUrls.map((url) => (
            <Img key={url} src={url} alt="" className="min-h-40 w-full" />
          ))}
        </section>
      )}

      {data.products.length > 0 && (
        <section className="mt-2 bg-white">
          <h2 className="border-b border-neutral-100 px-4 py-2.5 text-sm">这套用到的建材</h2>
          {data.products.map((p) => (
            <Link
              key={p.id}
              to="/products/$id"
              params={{ id: String(p.id) }}
              className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3 last:border-0"
            >
              <Img src={p.imageUrl} alt="" className="size-14 shrink-0 rounded object-cover" />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm leading-snug">{p.title}</p>
                <p className="mt-1 text-brand-600">
                  <Price cents={p.fromPrice} />
                  <span className="ml-1 text-xs text-ink-500">起</span>
                </p>
              </div>
            </Link>
          ))}
        </section>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function CaseDetailRoute() {
  return useIsDesktop() ? <DesktopCaseDetail /> : <CaseDetail />;
}

export const caseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cases/$slug",
  component: CaseDetailRoute,
});
